import { useState } from 'react';

export default function ComplianceChecklistSection() {
  const [checkedItems, setCheckedItems] = useState<{[key: string]: boolean}>({});

  const handleCheckboxChange = (itemId: string) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const quarterlyItems = [
    { id: 'q1', text: 'Review and update skills development budget allocation' },
    { id: 'q2', text: 'Monitor training programme progress and learner performance' },
    { id: 'q3', text: 'Assess compliance with planned training interventions' },
    { id: 'q4', text: 'Update employment equity and skills development records' },
    { id: 'q5', text: 'Review SETA communications and funding opportunities' },
    { id: 'q6', text: 'Conduct skills gap analysis updates' }
  ];

  const annualItems = [
    { id: 'a1', text: 'Submit Workplace Skills Plan (WSP) by April 30' },
    { id: 'a2', text: 'Submit Annual Training Report (ATR) by April 30' },
    { id: 'a3', text: 'Apply for discretionary grants (various deadlines)' },
    { id: 'a4', text: 'Conduct comprehensive skills audit' },
    { id: 'a5', text: 'Review and update SDF appointment and training' },
    { id: 'a6', text: 'Assess B-BBEE scorecard impact and optimization' },
    { id: 'a7', text: 'Plan next year\'s training calendar and budget' },
    { id: 'a8', text: 'Review training provider accreditations and contracts' }
  ];

  return (
    <section className="section-wrap bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-heading">
            SETA Compliance Checklist
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay on track with our comprehensive quarterly and annual compliance checklists
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Quarterly Checklist */}
          <div className="bg-gray-50 p-8 rounded-xl">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-[#2D7F3E] text-white rounded-lg flex items-center justify-center mr-4">
                <i className="ri-calendar-check-line text-xl"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Quarterly Checklist</h3>
                <p className="text-gray-600">Every 3 months</p>
              </div>
            </div>

            <div className="space-y-4">
              {quarterlyItems.map((item) => (
                <div key={item.id} className="flex items-start">
                  <input
                    type="checkbox"
                    id={item.id}
                    checked={checkedItems[item.id] || false}
                    onChange={() => handleCheckboxChange(item.id)}
                    className="mt-1 mr-3 w-5 h-5 text-[#2D7F3E] border-gray-300 rounded focus:ring-[#2D7F3E]"
                  />
                  <label 
                    htmlFor={item.id}
                    className={`text-gray-700 cursor-pointer ${checkedItems[item.id] ? 'line-through text-gray-500' : ''}`}
                  >
                    {item.text}
                  </label>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-[#2D7F3E] text-white rounded-lg">
              <p className="font-semibold text-center">
                Completed: {Object.values(checkedItems).filter(checked => quarterlyItems.some(item => checkedItems[item.id] && checked)).length} / {quarterlyItems.length}
              </p>
            </div>
          </div>

          {/* Annual Checklist */}
          <div className="bg-gray-50 p-8 rounded-xl">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-amber-500 text-white rounded-lg flex items-center justify-center mr-4">
                <i className="ri-calendar-event-line text-xl"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Annual Checklist</h3>
                <p className="text-gray-600">Key yearly milestones</p>
              </div>
            </div>

            <div className="space-y-4">
              {annualItems.map((item) => (
                <div key={item.id} className="flex items-start">
                  <input
                    type="checkbox"
                    id={item.id}
                    checked={checkedItems[item.id] || false}
                    onChange={() => handleCheckboxChange(item.id)}
                    className="mt-1 mr-3 w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <label 
                    htmlFor={item.id}
                    className={`text-gray-700 cursor-pointer ${checkedItems[item.id] ? 'line-through text-gray-500' : ''}`}
                  >
                    {item.text}
                  </label>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-amber-500 text-white rounded-lg">
              <p className="font-semibold text-center">
                Completed: {Object.values(checkedItems).filter(checked => annualItems.some(item => checkedItems[item.id] && checked)).length} / {annualItems.length}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-[#2D7F3E] to-green-600 text-white p-8 rounded-xl">
            <h3 className="text-2xl font-bold mb-4">Download Complete Compliance Guide</h3>
            <p className="text-green-100 mb-6 max-w-2xl mx-auto">
              Get our comprehensive SETA compliance guide with detailed checklists, templates, and expert tips for successful submissions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="btn btn-white">
                Download PDF Checklist
              </button>
              <button className="bg-green-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-800 transition-colors whitespace-nowrap">
                Get Excel Template
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}